import React, { useContext } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { isPositiveNumber } from "common/utils/numbers";
import {
  GetPaginatedPublicUsersForAccountQuery,
  useExportPublicUsersForAccountMutation,
  useGetPaginatedPublicUsersForAccountQuery,
} from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { AuthContext } from "../../Authorization/AuthContext";
import { Box } from "../../Common/__styles__/Layout";
import {
  useLegacyTableStateInURL,
  useManualPaginationConfig,
} from "../../Common/Tables/hooks";
import { Header, HeaderTitle } from "../__styles__/Content";
import PublicUsersTable from "./PublicUsersTable";
import { ExportButton } from "./shared";
import { useExportModal } from "./shared";

type PublicUser = NonNullable<
  GetPaginatedPublicUsersForAccountQuery["paginatedPublicUsersForAccount"]["data"][number]
>;

export const PublicUsers = () => {
  const { authorized } = useContext(AuthContext);

  const canExportPublicUsers = authorized({
    resource: RESOURCE_NAME.PUBLIC_USER,
    permission: "export",
  });

  const [initialTableState, setTableStateInURL] = useLegacyTableStateInURL({});

  const manualPaginationConfig = useManualPaginationConfig(
    initialTableState.pagination,
  );

  const variables = {
    page: manualPaginationConfig.pagination.pageIndex + 1,
    pageSize: manualPaginationConfig.pagination.pageSize,
  };

  const { data, loading, error, refetch } =
    useGetPaginatedPublicUsersForAccountQuery({
      variables,
    });

  const defaultData: PublicUser[] = [];

  const showExportPublicUsers =
    isPositiveNumber(
      data?.paginatedPublicUsersForAccount.pageInfo.totalCount,
    ) && canExportPublicUsers;

  if (error) return <div>Error loading public website guests.</div>;

  return (
    <Box>
      <Header>
        <HeaderTitle>Public website guests</HeaderTitle>
        {showExportPublicUsers ?
          <ExportPublicUsers />
        : null}
      </Header>
      <PublicUsersTable
        currentData={data?.paginatedPublicUsersForAccount.data ?? defaultData}
        loading={loading}
        manualPaginationConfig={{
          ...manualPaginationConfig,
          pageCount:
            data?.paginatedPublicUsersForAccount.pageInfo.totalPages ?? -1,
        }}
        initialTableState={initialTableState}
        setTableStateInURL={setTableStateInURL}
        onUpdate={() => refetch()}
      />
    </Box>
  );
};

const ExportPublicUsers = () => {
  const { addErrorToast } = useStatusToasts();
  const { setModalProps } = useExportModal();

  const [exportPublicUsersForAccount] = useExportPublicUsersForAccountMutation({
    onCompleted: () => {
      setModalProps({
        title: "Export data",
        message:
          "Your public website guests export is currently processing. Once your data is ready, we'll email a link to download your file.",
      });
    },
    onError: () => {
      addErrorToast(
        "There was an error with your public website guests export. Please try again. If the problem persists, please email support@withforerunner.com",
      );
    },
  });

  return (
    <ExportButton
      onClickExport={async () => {
        await exportPublicUsersForAccount();
      }}
      ariaLabel="Export public website guests"
    />
  );
};

export default PublicUsers;
